import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import jwt_decode from 'jwt-decode';
import { throwError, timer } from 'rxjs';
import { retry, tap, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './local-storage.service';

export const WELBY_TOKEN = 'welby-token';

// Transient server/network failures worth retrying. A 401 (wrong password) or any
// other 4xx is deliberately excluded: retrying it only piles load onto the auth path.
const TRANSIENT_STATUSES = new Set([0, 502, 503, 504]);

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private jwtToken: string | null = null;
  private decodedToken: { [key: string]: string } | null = null;

  constructor(
    private localStorage: LocalStorageService,
    private http: HttpClient
  ) {
    const token = this.localStorage.get(WELBY_TOKEN);
    if (token) {
      this.jwtToken = token;
    }
  }

  /**
   * `firebaseIdToken` is a fresh ID token taken after the second factor. The backend checks it to
   * decide whether the session passed MFA, which staff sessions must once enforcement is on.
   */
  requestNewToken(username, pwHash, firebaseIdToken?: string): Promise<{ access_token: string }> {
    const endpoint = environment.welbyEndpoint + '/api/v1/auth/authorize';
    const body = firebaseIdToken ? { username, password: pwHash, firebaseIdToken } : { username, password: pwHash };
    const { maxAttempts } = environment.authRetry;
    return this.http
      .post<{ access_token: string }>(endpoint, body)
      .pipe(
        tap((result) => this.setToken(result.access_token)),
        // Only transient failures are retried, spaced with exponential backoff + jitter so a
        // degraded backend is not hammered. Wrong-password (401) and other 4xx are not retried.
        retry({
          count: maxAttempts,
          delay: (error, retryCount) => {
            if (!this.isTransient(error)) {
              return throwError(() => error);
            }
            return timer(this.backoffDelay(retryCount));
          },
        })
      )
      .toPromise();
  }

  /**
   * Reports a sign-in that Firebase Auth rejected, so the backend can count it toward the account
   * lockout (5 in a row locks the account for 15 minutes). Firebase checks the password before this
   * app ever calls the backend, so without this report a wrong password is invisible to it.
   * Never throws: a failed report must not change what the user sees for the failed sign-in itself.
   */
  async reportFailedLogin(username: string): Promise<{ locked: boolean }> {
    const endpoint = environment.welbyEndpoint + '/api/v1/auth/login-failed';
    try {
      // Bounded, so a slow backend cannot hold up the "wrong password" message.
      const result = await this.http.post<{ locked: boolean }>(endpoint, { username }).pipe(timeout(3000)).toPromise();
      return { locked: !!result?.locked };
    } catch {
      return { locked: false };
    }
  }

  setToken(token: string): void {
    if (token) {
      this.jwtToken = token;
      this.localStorage.set(WELBY_TOKEN, token);
    }
  }

  getToken(): string | null {
    return this.jwtToken;
  }

  destroyToken(): void {
    this.jwtToken = null;
    this.localStorage.remove(WELBY_TOKEN);
  }

  decodeToken(): void {
    if (this.jwtToken) {
      this.decodedToken = jwt_decode(this.jwtToken);
    }
  }

  getDecodeToken(): string {
    return jwt_decode(this.jwtToken as string);
  }

  getExpiryTime(): string | null {
    this.decodeToken();
    return this.decodedToken ? this.decodedToken.exp : null;
  }

  tokenIsExpired(): boolean {
    if (!this.jwtToken) {
      return true;
    }
    const expiryTime: number = parseInt(this.getExpiryTime() as string, 10);
    if (expiryTime) {
      return 1000 * expiryTime - new Date().getTime() < 5000;
    } else {
      return false;
    }
  }

  private isTransient(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || !TRANSIENT_STATUSES.has(error.status)) {
      return false;
    }
    // The backend's own "could not run the MFA check" 503 has passed the login throttle (5/min per
    // user): retrying it in the same second would burn that budget and turn the user's next manual
    // attempt into a 429. Only 503s that never reached the API are worth retrying.
    return error.error?.message !== 'MFA_CHECK_UNAVAILABLE';
  }

  /** Exponential backoff (baseDelayMs * 2^(retryCount-1), capped at maxDelayMs) with 50-100% jitter. */
  private backoffDelay(retryCount: number): number {
    const { baseDelayMs, maxDelayMs } = environment.authRetry;
    const capped = Math.min(baseDelayMs * 2 ** (retryCount - 1), maxDelayMs);
    return capped / 2 + Math.random() * (capped / 2);
  }
}
